import React, { useEffect, useState } from 'react';
import { Card } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Switch } from '../../../components/ui/switch';
import { Loader2, Save } from 'lucide-react';
import { adminAPI } from '../../../services/api';
import { toast } from '../../../hooks/use-toast';
import { ModuleShell } from './ModuleShell';

const Field = ({ field, value, onChange }) => {
  if (field.type === 'bool') {
    return (
      <div className="flex items-center justify-between py-3 border-b border-gray-700/50">
        <div>
          <p className="text-sm text-gray-200">{field.label}</p>
          {field.hint && <p className="text-xs text-gray-500">{field.hint}</p>}
        </div>
        <Switch checked={!!value} onCheckedChange={onChange} data-testid={`module-field-${field.key}`} />
      </div>
    );
  }
  return (
    <div>
      <label className="text-sm text-gray-400 block mb-1.5">{field.label}</label>
      <Input type={field.type === 'number' ? 'number' : 'text'} value={value ?? ''} data-testid={`module-field-${field.key}`}
        onChange={e => onChange(field.type === 'number' ? e.target.value : e.target.value)}
        className="bg-gray-700 border-gray-600 text-white" />
      {field.hint && <p className="text-xs text-gray-500 mt-1">{field.hint}</p>}
    </div>
  );
};

export const ConfigModulePage = ({ module, title, description, note, fields }) => {
  const [state, setState] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    adminAPI.getModuleConfig(module).then(setState).catch(() => setState({ config: {}, error: true }));
  }, [module]);

  const set = (k, v) => setState(s => ({ ...s, config: { ...s.config, [k]: v } }));

  const save = async () => {
    setSaving(true);
    try {
      setState(await adminAPI.saveModuleConfig(module, state.config));
      toast({ title: 'Configuración guardada' });
    } catch (e) {
      toast({ title: e.response?.data?.detail || 'Error al guardar', variant: 'destructive' });
    } finally { setSaving(false); }
  };

  return (
    <ModuleShell title={title} description={description} note={note} testid={`module-${module}`}>
      <Card className="bg-gray-800 border-gray-700 p-6">
        {!state ? <Loader2 className="w-6 h-6 animate-spin text-gray-400" /> : (
          <>
            <p className="text-xs text-gray-500 mb-4" data-testid="module-config-status">
              {state.configured ? `Guardado por última vez: ${new Date(state.updated_at).toLocaleString('es-MX')}` : 'Valores por defecto — aún no guardado'}
            </p>
            <div className="grid sm:grid-cols-2 gap-4">
              {fields.filter(f => f.type !== 'bool').map(f => <Field key={f.key} field={f} value={state.config[f.key]} onChange={v => set(f.key, v)} />)}
            </div>
            <div className="mt-4">
              {fields.filter(f => f.type === 'bool').map(f => <Field key={f.key} field={f} value={state.config[f.key]} onChange={v => set(f.key, v)} />)}
            </div>
            <Button onClick={save} disabled={saving} className="mt-6 bg-orange-500 hover:bg-orange-600" data-testid="module-save-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />} Guardar
            </Button>
          </>
        )}
      </Card>
    </ModuleShell>
  );
};
